/**
 * Notification type keys shared across producers (settings, billing, auth, the
 * warmup-worker state machine) and the notifications consumer in apps/api. A
 * pure string union — no runtime value. Moved here from `warmup.interface.ts`
 * when notifications grew past warmup into membership + billing events.
 *
 * `ApiNotificationPayload.type` is typed to THIS union, so a producer emitting
 * an unlisted name is a compile error. That was not true through the initial
 * build (the payload field was a bare `string`), which is how the seven
 * state-machine types below came to be emitted while absent here — every one of
 * them fell through `categoryOf` to `SYSTEM`. Keep the payload typed.
 */
export type NotificationType =
  // Warmup (produced by apps/warmup-worker, except ACCOUNT_CONNECTED which is
  // produced by apps/api)
  | 'WARMUP_PAUSED'
  | 'WARMUP_STARTED'
  | 'ACCOUNT_DISCONNECTED'
  | 'ACCOUNT_CONNECTED'
  | 'DNS_FAILURE'
  | 'PAYMENT_FAILED'
  | 'PLAN_DOWNGRADED'
  | 'INVITE_RECEIVED'
  | 'WORKSPACE_DELETED'
  | 'CSV_IMPORT_COMPLETE'
  | 'WARMUP_SETTINGS_BULK_COMPLETE'
  | 'WARMUP_ENABLE_BULK_COMPLETE'
  | 'WARMUP_PAUSE_BULK_COMPLETE'
  | 'ACCOUNT_DELETE_BULK_COMPLETE'
  // Warmup state machine (apps/warmup-worker state-machine + auto-resume +
  // recipient-quarantine). The pause/bounce/DNS reasons above come from
  // `buildPauseNotification`'s reason→type map; these are emitted directly.
  | 'WARMUP_SUSPENDED'
  // A suspension whose cause is repeated send-time failures
  // (`SEND_FAILURES_EXCEEDED`, email-worker's `SendFailureTracker`). Emitted by
  // `buildSuspendNotification` in place of WARMUP_SUSPENDED so the user can
  // toggle "Failed to send warm-up email" separately from auto-suspension.
  | 'WARMUP_SEND_FAILED'
  | 'WARMUP_SUSPEND_MAX_DURATION'
  | 'WARMUP_THROTTLED'
  | 'WARMUP_THROTTLE_RECOVERED'
  | 'WARMUP_GRADUATED'
  | 'WARMUP_REACTIVATED'
  | 'WARMUP_REPUTATION_DECLINING'
  | 'WARMUP_RECIPIENT_QUARANTINED'
  // Membership (produced by apps/api settings + auth)
  | 'MEMBER_JOINED'
  | 'MEMBER_REMOVED'
  | 'MEMBER_ROLE_CHANGED'
  | 'ORG_MEMBER_REMOVED'
  | 'ORG_ROLE_CHANGED'
  | 'OWNERSHIP_TRANSFERRED'
  // Billing (produced by apps/api billing)
  | 'PLAN_UPGRADED'
  | 'SUBSCRIPTION_CANCELED'
  // APUS per-org extensions (docs/plans/2026-09-17-apus-plan-change-and-extensions.md).
  // Produced by apps/api the moment support's unlock of a feature/permission is
  // approved. A daily-limit change sends no notification.
  | 'FEATURE_UNLOCKED'
  // Billing lifecycle requests (apps/api billing, OWNER-initiated). These notify
  // the org owner + admins (minus the actor) at request time — distinct from the
  // applied-state types above (SUBSCRIPTION_CANCELED etc.) which fire from the
  // webhook once Paddle applies the change.
  | 'SUBSCRIPTION_UPGRADE_REQUESTED'
  | 'SUBSCRIPTION_CANCEL_SCHEDULED'
  | 'SUBSCRIPTION_PAUSE_SCHEDULED'
  | 'SUBSCRIPTION_RESUMED'
  | 'SUBSCRIPTION_SCHEDULED_CHANGE_REVOKED'
  | 'SUBSCRIPTION_DISCOUNT_REDEEMED'
  // Usage nudges (apps/api billing `UsageService.sweep`, one per limit per tier
  // per org-timezone day). The four tiers are 70 / 80 / 90 / 100 percent of an
  // org-level numeric limit — see `USAGE_TIER_NOTIFICATION_TYPE` in
  // apps/api/src/modules/billing/usage-nudge.constants.ts, which maps every tier
  // to exactly one of these.
  | 'USAGE_LIMIT_APPROACHING'
  | 'USAGE_LIMIT_WARNING'
  | 'USAGE_LIMIT_CRITICAL'
  | 'USAGE_LIMIT_REACHED';

/**
 * The category axis every `NotificationType` maps to — used for filtering and
 * per-category preferences. `SYSTEM` is reserved (no producers yet) and is also
 * the fallback for any unmapped type. String values match the persisted
 * `notifications.category` column and the wire contract.
 */
export enum NotificationCategory {
  MEMBERSHIP = 'MEMBERSHIP',
  BILLING = 'BILLING',
  WARMUP = 'WARMUP',
  SYSTEM = 'SYSTEM',
}

/**
 * The user-facing preference axis — one row per toggle on Settings →
 * Notifications. Distinct from `NotificationCategory` (the feed-filter axis
 * stamped on each notification row): a category groups many types for
 * filtering, while a preference key is what one In-app / Email switch pair
 * controls. Most keys are granular (one type), two are grouped
 * (`WORKSPACE_ACTIVITY`, `TEAM_ACTIONS`). Every `NotificationType` maps to
 * exactly one key (`NOTIFICATION_TYPE_PREFERENCE_KEY` in apps/api). String
 * values are persisted in `notificationPreferences.category` and are the wire
 * contract of `GET/PUT /notifications/preferences`.
 */
export enum NotificationPreferenceKey {
  WORKSPACE_ACTIVITY = 'WORKSPACE_ACTIVITY',
  TEAM_ACTIONS = 'TEAM_ACTIONS',
  DOMAIN_ISSUE = 'DOMAIN_ISSUE',
  DELIVERABILITY_DECLINING = 'DELIVERABILITY_DECLINING',
  SEND_FAILED = 'SEND_FAILED',
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  AUTO_SUSPENDED = 'AUTO_SUSPENDED',
  VOLUME_REDUCED = 'VOLUME_REDUCED',
  RECIPIENT_QUARANTINED = 'RECIPIENT_QUARANTINED',
  WARMUP_REACTIVATED = 'WARMUP_REACTIVATED',
  MAINTENANCE = 'MAINTENANCE',
  /** Locked: always delivered, never user-configurable. */
  BILLING = 'BILLING',
}

/**
 * Bounds for the per-user "Deliverability declining" threshold — the number of
 * percentage points inbox placement must fall below 100% before
 * `WARMUP_REPUTATION_DECLINING` reaches that user. The floor matches the
 * warmup-worker's own alert bar (`DECAY_ALERT_THRESHOLD` = 90%, i.e. a 10-point
 * drop), below which no alert is ever produced.
 */
export const DECLINE_THRESHOLD_PCT = {
  MIN: 10,
  MAX: 50,
  DEFAULT: 20,
} as const;

/**
 * Alert severity derived from a notification's `type` (an in-code map, no DB
 * column) — surfaced on the read DTO so alerts render visually distinct from
 * plain notifications. A notification with NO severity is a plain notification
 * (`severity = null`); only `CRITICAL` / `WARNING` types are alerts. Values are
 * lowercase (they're a UI/wire vocabulary, not a persisted column).
 */
export enum NotificationSeverity {
  CRITICAL = 'critical',
  WARNING = 'warning',
}

/** The read-state filter on the notification feed (`GET /notifications`). */
export enum NotificationReadState {
  ALL = 'all',
  UNREAD = 'unread',
  READ = 'read',
}
